import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ContentBox,
  ContentPage,
  DatePicker,
  FullTable,
  NoResults,
  SelectorPageStatus,
  Skeleton,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import TimeClockMirrorTable from "@components/TimeClockMirrorTable";
import { ADJUSTMENT_COLUMNS } from "@components/ClockAdjustments";
import { escapeHtml, formatHours, usePageHeader } from "@components/ow";
import { WEEKDAY_LABELS, currentReference } from "@helpers/timeClock";
import { exportMirror } from "@helpers/timeClockSheet";
import { DetailTabs } from "../../Common/ContractDetail";
import ClockModal from "./ClockModal";
import RequestAdjustmentModal from "./RequestAdjustmentModal";

// Ponto do colaborador CLT (modelo Detalhe do DS OneWork): o bloco "Hoje" fixo no
// topo (relógio, registrar ponto e as batidas do dia) e as abas Espelho de ponto e
// Pedidos de ajuste. O espelho é do mês calendário (o fechamento da empresa só o
// ADM e o gestor de ponto veem).

const PAGE_HREF = "/e/time-clock";

// Página da API por requisição: uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 10;

const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Pendente", value: "PENDING" },
  { label: "Aprovado", value: "APPROVED" },
  { label: "Reprovado", value: "REJECTED" },
];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
  align-items: ${(p) => p.$align || "stretch"};
`;

const Inline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const Today = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 30px;
`;

const ClockSide = styled(Stack)`
  padding-right: 30px;
  border-right: 1px solid ${({ theme }) => theme.colors.N100};
`;

const Filters = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
`;

const CompetenceField = styled.div`
  width: 200px;
`;

/** 'YYYY-MM' ↔ Date do primeiro dia, para o DatePicker de competência. */
const referenceToDate = (reference) => {
  const [year, month] = reference.split("-").map(Number);
  return new Date(year, month - 1, 1);
};
const dateToReference = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/** Fuso do dispositivo: "America/Sao_Paulo (UTC-03:00)". */
function deviceTimeZone() {
  const minutes = -new Date().getTimezoneOffset();
  const abs = Math.abs(minutes);
  const offset = `UTC${minutes >= 0 ? "+" : "-"}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
  return `${Intl.DateTimeFormat().resolvedOptions().timeZone} (${offset})`;
}

/** Relógio do dispositivo; só ele se renderiza a cada segundo. */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const time = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const day = now.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  return (
    <Stack $gap="4px">
      <Text $text={time} $variant="display_medium" $color="N900" />
      <Text $text={day.charAt(0).toUpperCase() + day.slice(1)} $variant="paragraph_small_medium" $color="N500" />
    </Stack>
  );
}

const ENTRY_COLUMNS = [
  { id: "order", header: "#", size: 50, cell: ({ row }) => `${row.index + 1}ª` },
  {
    id: "time",
    header: "Horário",
    size: 90,
    cell: ({ row }) => <Text $text={row.original.time} $variant="paragraph_small_bold" $color="N900" />,
  },
  {
    id: "source",
    header: "Origem",
    size: 100,
    cell: ({ row }) =>
      row.original.source === "ADJUSTMENT" ? (
        <StatePill $text="Ajuste" $variant="blue" $size="small" />
      ) : (
        <StatePill $text="Ponto" $variant="gray" $size="small" />
      ),
  },
  {
    id: "note",
    header: "Observação",
    size: 300,
    cell: ({ row }) => (
      <Text $text={escapeHtml(row.original.note || "-")} $variant="paragraph_small_medium" $color="N500" />
    ),
  },
];

/** Batidas de hoje, o trabalhado até agora e a jornada do contrato. */
function TodayEntries({ today }) {
  const dailyMinutes = Math.round(parseFloat(today.contract?.clt_daily_hours || 0) * 60);
  const workdays = (today.contract?.clt_workdays || []).map((day) => WEEKDAY_LABELS[day]).join(", ");
  const dayOff = today.day_off;

  return (
    <Stack $gap="12px">
      <Inline>
        <Text $text="Batidas de hoje" $variant="caption_bold" $color="N700" />
        {today.open ? <StatePill $text="Em jornada" $variant="blue" $size="small" /> : null}
        {dayOff ? (
          <>
            <StatePill
              $text={dayOff.hours_off ? `Folga de ${formatHours(dayOff.hours_off * 60)}` : "Folga"}
              $variant="gray"
              $size="small"
            />
            <Text $text={escapeHtml(dayOff.description || "Folga da empresa")} $variant="caption_medium" $color="N500" />
          </>
        ) : null}
      </Inline>
      <Stack $gap="2px">
        <Text
          $text="Trabalhado <strong>%s</strong> de %s"
          $replaces={[formatHours(today.worked_minutes || 0), formatHours(today.expected_minutes || 0)]}
          $variant="paragraph_small_medium"
          $color="N700"
          $colorEmphasis="N900"
        />
        <Text
          $text="Jornada de %s por dia · %s"
          $replaces={[formatHours(dailyMinutes), workdays || "-"]}
          $variant="caption_medium"
          $color="N400"
        />
      </Stack>
      {today.entries?.length ? (
        <BasicTable data={today.entries} columns={ENTRY_COLUMNS} getRowId={(entry) => String(entry.id)} />
      ) : (
        <Text $text="Nenhuma batida hoje." $variant="paragraph_small_medium" $color="N400" />
      )}
    </Stack>
  );
}

/** Pedidos de ajuste do próprio colaborador, com a paginação do back. */
function AdjustmentRequests({ onRequest }) {
  const timeClockService = useService("timeClock");
  const [status, setStatus] = useState(STATUS_DATA[0]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [data, setData] = useState({ totalRegisters: 0, list: [] });
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (trocas seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);

  useEffect(() => {
    (async () => {
      const request = ++lastRequest.current;
      setLoading(true);
      const params = [`page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
      if (status.value) params.push(`status=${status.value}`);
      const result = await timeClockService.listAdjustments(params.join("&"));
      if (request !== lastRequest.current) return;
      // Em erro, o sendError do service já mostrou o toast
      setData(result && !result.error && result.data?.list ? result.data : { totalRegisters: 0, list: [] });
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.value, pagination.pageIndex, pagination.pageSize]);

  function changeStatus(option) {
    setStatus(option);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  const noResults = status.value ? (
    <NoResults notResultText="Nenhum pedido encontrado" includeText="Ver todos" includeAction={() => changeStatus(STATUS_DATA[0])} />
  ) : (
    <NoResults
      notResultText="Nenhum pedido de ajuste. Esqueceu de bater o ponto? Solicite a inclusão da batida."
      includeText="Solicitar ajuste"
      includeAction={onRequest}
    />
  );

  return (
    <Stack>
      <SelectorPageStatus options={STATUS_DATA} selected={status} onChange={changeStatus} />
      <FullTable
        data={data.list}
        columns={ADJUSTMENT_COLUMNS}
        rowCount={data.totalRegisters}
        pagination={pagination}
        setPagination={setPagination}
        getRowId={(row) => String(row.id)}
        isLoading={loading}
        noResults={noResults}
      />
    </Stack>
  );
}

export default function TimeClockEmployee() {
  const timeClockService = useService("timeClock");

  usePageHeader({ title: "Ponto", breadcrumb: [{ page: "Ponto", route: PAGE_HREF }] });

  const [today, setToday] = useState(null);
  const [todayError, setTodayError] = useState(null);
  const [activeTab, setActiveTab] = useState("espelho");
  const [reference, setReference] = useState(currentReference());
  const [mirror, setMirror] = useState(null);
  const [mirrorLoading, setMirrorLoading] = useState(true);
  const [showClock, setShowClock] = useState(false);
  // Pedido de ajuste aberto: { date } com o dia do espelho ou { date: null }
  const [request, setRequest] = useState(null);
  // A lista de pedidos volta a "Todos" e à primeira página depois de um pedido novo
  const [requestsKey, setRequestsKey] = useState(0);

  useEffect(() => {
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (today) fetchMirror();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, Boolean(today)]);

  async function fetchToday() {
    const result = await timeClockService.today();
    // Em erro (sem contrato CLT ativo, por exemplo), o sendError do service já mostrou o toast
    if (!result || result.error) {
      setTodayError(result?.msg || "Erro ao buscar batidas de hoje");
      return;
    }
    setTodayError(null);
    setToday(result.data);
  }

  async function fetchMirror() {
    setMirrorLoading(true);
    const result = await timeClockService.getMirror(null, reference);
    // Em erro, o sendError do service já mostrou o toast
    setMirror(result && !result.error ? result.data : null);
    setMirrorLoading(false);
  }

  function handleExport() {
    try {
      exportMirror(mirror);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao exportar Excel");
    }
  }

  if (todayError) {
    const noContract = /contrato CLT/i.test(todayError);
    return (
      <ContentPage>
        <ContentBox>
          {noContract ? (
            <NoResults notResultText="Você não tem contrato CLT ativo nesta empresa. O ponto aparece aqui quando a empresa cadastrar o seu contrato CLT." />
          ) : (
            <NoResults notResultText="Não foi possível carregar o seu ponto" includeText="Tentar de novo" includeAction={fetchToday} />
          )}
        </ContentBox>
      </ContentPage>
    );
  }

  if (!today) {
    return (
      <ContentPage aria-busy="true">
        <ContentBox>
          <Skeleton $height={200} />
        </ContentBox>
        <ContentBox>
          <Stack>
            <Skeleton $height={48} />
            <Skeleton $height={420} />
          </Stack>
        </ContentBox>
      </ContentPage>
    );
  }

  const mirrorTab = (
    <Stack>
      <Filters>
        <CompetenceField>
          <DatePicker
            $idForm="time-clock-competence"
            $label="Competência"
            $variant="default"
            $isCompetence
            $date={referenceToDate(reference)}
            $setDate={(date) => date && setReference(dateToReference(date))}
          />
        </CompetenceField>
        <div>
          <Button
            $text="Exportar espelho"
            $variant="outline"
            $size="small"
            $noWidth
            $isDisabled={!mirror || mirrorLoading}
            $action={handleExport}
          />
        </div>
      </Filters>
      {mirrorLoading ? (
        <Stack>
          <Skeleton $height={56} />
          <Skeleton $height={420} />
        </Stack>
      ) : mirror ? (
        <TimeClockMirrorTable mirror={mirror} onRequestAdjustment={(date) => setRequest({ date })} />
      ) : (
        <NoResults notResultText="Nenhum dado para o período selecionado" />
      )}
    </Stack>
  );

  const tabs = [
    { value: "espelho", label: "Espelho de ponto", content: mirrorTab },
    {
      value: "pedidos",
      label: "Pedidos de ajuste",
      content: <AdjustmentRequests key={requestsKey} onRequest={() => setRequest({ date: null })} />,
    },
  ];

  return (
    <ContentPage>
      <ContentBox>
        <Today>
          <ClockSide $gap="16px" $align="flex-start">
            <LiveClock />
            <Button $text="Registrar ponto" $variant="primary" $noWidth $action={() => setShowClock(true)} />
            <Text
              $text="O horário gravado é o do servidor. Seu dispositivo está em %s."
              $replaces={[deviceTimeZone()]}
              $variant="caption_medium"
              $color="N400"
            />
          </ClockSide>
          <TodayEntries today={today} />
        </Today>
      </ContentBox>

      <ContentBox>
        <DetailTabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          actions={
            <Button $text="Solicitar ajuste" $variant="outline" $noWidth $action={() => setRequest({ date: null })} />
          }
        />
      </ContentBox>

      {showClock ? (
        <ClockModal
          onClose={() => setShowClock(false)}
          onSaved={() => {
            setShowClock(false);
            fetchToday();
            fetchMirror();
          }}
        />
      ) : null}

      {request ? (
        <RequestAdjustmentModal
          initialDate={request.date}
          startDate={mirror?.contract?.start_date}
          onClose={() => setRequest(null)}
          onSaved={() => {
            setRequest(null);
            setRequestsKey((key) => key + 1);
            setActiveTab("pedidos");
          }}
        />
      ) : null}
    </ContentPage>
  );
}
